import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { useUser } from '../contexts/UserContext';
import { PageHeader, Button, Alert, LoadingState, EmptyState, KpiCard } from './ui';

export default function HREvaluation() {
  const { user } = useUser(); // アクセス制御の準備のため保持
  const [evaluations, setEvaluations] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingEvaluation, setEditingEvaluation] = useState(null);
  const [filters, setFilters] = useState({
    engineer_id: '',
    evaluation_type: '',
    date_from: '',
    date_to: ''
  });
  const [groupedByEngineer, setGroupedByEngineer] = useState({});
  const [viewMode, setViewMode] = useState('list'); // 'list', 'dashboard'
  const [selectedEvaluation, setSelectedEvaluation] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [engineerSearch, setEngineerSearch] = useState("");
  const [showEngineerDropdown, setShowEngineerDropdown] = useState(false);
  const [expandedEngineers, setExpandedEngineers] = useState(new Set());

  // フォーム状態
  const [formData, setFormData] = useState({
    engineer: '',
    evaluation_date: '',
    evaluation_type: 'evaluation_interview',
    technical_skill: '',
    communication_skill: '',
    motivation: '',
    leadership: '',
    problem_solving: '',
    overall_rating: '',
    strengths: '',
    improvement_areas: '',
    goals_next_period: '',
    notes: ''
  });

  const fetchEvaluations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      // 人事評価のみを取得するフィルターを強制追加
      params.append('interview_type', 'evaluation_interview');
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await fetch(`/api/interviews/?${params}`);
      if (response.ok) {
        const data = await response.json();
        const evaluationsData = data.results || data;
        console.log('HR評価データ:', evaluationsData);
        setEvaluations(evaluationsData);
        
        // エンジニア別にグループ化
        const grouped = evaluationsData.reduce((acc, evaluation) => {
          const engineerName = evaluation.engineer_name || '未設定';
          if (!acc[engineerName]) {
            acc[engineerName] = [];
          }
          acc[engineerName].push(evaluation);
          return acc;
        }, {});
        
        // 各エンジニアの評価を日付順でソート
        Object.keys(grouped).forEach(engineerName => {
          grouped[engineerName].sort((a, b) => new Date(b.interview_date) - new Date(a.interview_date));
        });
        
        setGroupedByEngineer(grouped);
      }
    } catch (error) {
      setError('人事評価データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchEvaluations();
    fetchEngineers();
  }, [fetchEvaluations]);

  const fetchEngineers = async () => {
    try {
      const response = await fetch('/api/engineers/');
      if (response.ok) {
        const data = await response.json();
        setEngineers(data.results || data);
      }
    } catch (error) {
      console.error('エンジニア情報の取得に失敗:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const requestData = {
      engineer: formData.engineer, // エンジニアID
      interview_type: 'evaluation_interview',
      interview_date: formData.evaluation_date,
      // 評価項目を数値に変換（空文字列の場合はnull）
      technical_skill: formData.technical_skill ? parseInt(formData.technical_skill) : null,
      communication_skill: formData.communication_skill ? parseInt(formData.communication_skill) : null,
      motivation: formData.motivation ? parseInt(formData.motivation) : null,
      leadership: formData.leadership ? parseInt(formData.leadership) : null,
      problem_solving: formData.problem_solving ? parseInt(formData.problem_solving) : null,
      overall_rating: formData.overall_rating ? parseInt(formData.overall_rating) : null,
      // テキストフィールド
      strengths: formData.strengths || null,
      improvement_areas: formData.improvement_areas || null,
      goals_next_period: formData.goals_next_period || null,
      notes: formData.notes || null,
      // お客様面談用フィールドは空で送信
      client_company: null,
      result: 'pass', // 人事評価では基本的に実施済み
      rejection_reason: null,
      improvement_points: null,
      next_action: null
    };
    
    console.log('📤 送信データ:', requestData);
    
    try {
      const url = editingEvaluation 
        ? `/api/interviews/${editingEvaluation.id}/`
        : '/api/interviews/';
      
      const method = editingEvaluation ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (response.ok) {
        fetchEvaluations();
        setShowForm(false);
        setEditingEvaluation(null);
        resetForm();
      } else {
        const errorData = await response.json();
        console.error('API エラー:', errorData);
        setError(`人事評価の保存に失敗しました: ${errorData.message || response.statusText}`);
      }
    } catch (error) {
      console.error('ネットワークエラー:', error);
      setError(`ネットワークエラーが発生しました: ${error.message}`);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`/api/interviews/${id}/`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setConfirmDeleteId(null);
        fetchEvaluations();
      }
    } catch (error) {
      setError('削除に失敗しました');
    }
  };

  const exportToExcel = () => {
    try {
      const allEvaluations = Object.values(groupedByEngineer).flat();
      const ratingLabel = (v) => ({ 1: '要改善', 2: 'やや不足', 3: '標準', 4: '良好', 5: '優秀' }[v] || '');
      const rows = allEvaluations.map(e => ({
        エンジニア名: e.engineer_name || '',
        評価日: e.interview_date || '',
        技術力: e.technical_skill ? `${e.technical_skill} (${ratingLabel(e.technical_skill)})` : '',
        'コミュニケーション力': e.communication_skill ? `${e.communication_skill} (${ratingLabel(e.communication_skill)})` : '',
        'やる気意欲': e.motivation ? `${e.motivation} (${ratingLabel(e.motivation)})` : '',
        'リーダーシップ': e.leadership ? `${e.leadership} (${ratingLabel(e.leadership)})` : '',
        '問題解決力': e.problem_solving ? `${e.problem_solving} (${ratingLabel(e.problem_solving)})` : '',
        '総合評価': e.overall_rating ? `${e.overall_rating} (${ratingLabel(e.overall_rating)})` : '',
        '強み': e.strengths || '',
        '改善領域': e.improvement_areas || '',
        '次期目標': e.goals_next_period || '',
        '備考': e.notes || '',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '人事評価一覧');
      XLSX.writeFile(wb, `人事評価一覧_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      setError('Excelエクスポートに失敗しました');
    }
  };

  const handleEdit = (evaluation) => {
    console.log('編集対象データ:', evaluation);
    setEditingEvaluation(evaluation);
    setFormData({
      engineer: evaluation.engineer,
      evaluation_date: evaluation.interview_date,
      evaluation_type: evaluation.interview_type,
      // 数値を文字列に変換（null/undefinedの場合は空文字）
      technical_skill: evaluation.technical_skill ? String(evaluation.technical_skill) : '',
      communication_skill: evaluation.communication_skill ? String(evaluation.communication_skill) : '',
      motivation: evaluation.motivation ? String(evaluation.motivation) : '',
      leadership: evaluation.leadership ? String(evaluation.leadership) : '',
      problem_solving: evaluation.problem_solving ? String(evaluation.problem_solving) : '',
      overall_rating: evaluation.overall_rating ? String(evaluation.overall_rating) : '',
      strengths: evaluation.strengths || '',
      improvement_areas: evaluation.improvement_areas || '',
      goals_next_period: evaluation.goals_next_period || '',
      notes: evaluation.notes || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      engineer: '',
      evaluation_date: '',
      evaluation_type: 'evaluation_interview',
      technical_skill: '',
      communication_skill: '',
      motivation: '',
      leadership: '',
      problem_solving: '',
      overall_rating: '',
      strengths: '',
      improvement_areas: '',
      goals_next_period: '',
      notes: ''
    });
    setEngineerSearch('');
    setShowEngineerDropdown(false);
  };

  const toggleEngineer = (name) => {
    setExpandedEngineers(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const getSkillRatingText = (rating) => {
    // 数値と文字列の両方に対応
    const ratingStr = String(rating);
    switch (ratingStr) {
      case '1': return '1（要改善）';
      case '2': return '2（やや不足）';
      case '3': return '3（標準）';
      case '4': return '4（良好）';
      case '5': return '5（優秀）';
      default: return '未評価';
    }
  };

  // 📊 人事評価ダッシュボード表示関数
  const renderDashboardView = () => {
    // 全評価データを平坦化
    const allEvaluations = Object.values(groupedByEngineer).flat();
    
    // 統計データ計算
    const stats = {
      totalEvaluations: allEvaluations.length,
      totalEngineers: Object.keys(groupedByEngineer).length,
      avgTechnicalSkill: allEvaluations.filter(e => e.technical_skill).length > 0 
        ? (allEvaluations.reduce((acc, e) => acc + (e.technical_skill || 0), 0) / allEvaluations.filter(e => e.technical_skill).length).toFixed(1)
        : 0,
      avgCommunicationSkill: allEvaluations.filter(e => e.communication_skill).length > 0
        ? (allEvaluations.reduce((acc, e) => acc + (e.communication_skill || 0), 0) / allEvaluations.filter(e => e.communication_skill).length).toFixed(1)
        : 0,
      avgOverallRating: allEvaluations.filter(e => e.overall_rating).length > 0
        ? (allEvaluations.reduce((acc, e) => acc + (e.overall_rating || 0), 0) / allEvaluations.filter(e => e.overall_rating).length).toFixed(1)
        : 0,
      highPerformers: allEvaluations.filter(e => e.overall_rating >= 4).length,
      needsImprovement: allEvaluations.filter(e => e.overall_rating <= 2).length,
      thisMonth: allEvaluations.filter(e => {
        const evalDate = new Date(e.interview_date);
        const now = new Date();
        return evalDate.getMonth() === now.getMonth() && evalDate.getFullYear() === now.getFullYear();
      }).length
    };

    // エンジニア別成長分析
    const engineerGrowthStats = Object.entries(groupedByEngineer).map(([name, evals]) => {
      const sortedEvals = evals.sort((a, b) => new Date(a.interview_date) - new Date(b.interview_date));
      const latest = sortedEvals[sortedEvals.length - 1];
      const previous = sortedEvals[sortedEvals.length - 2];
      
      return {
        name,
        evaluationCount: evals.length,
        latestOverall: latest?.overall_rating || 0,
        latestTechnical: latest?.technical_skill || 0,
        latestCommunication: latest?.communication_skill || 0,
        growth: previous && latest ? (latest.overall_rating || 0) - (previous.overall_rating || 0) : 0,
        lastEvaluationDate: latest?.interview_date,
        hasStrengths: latest?.strengths ? true : false,
        hasGoals: latest?.goals_next_period ? true : false
      };
    }).sort((a, b) => b.latestOverall - a.latestOverall);

    // スキル分布データ
    const skillDistribution = {
      technical: [1, 2, 3, 4, 5].map(rating => ({
        rating,
        count: allEvaluations.filter(e => e.technical_skill === rating).length
      })),
      communication: [1, 2, 3, 4, 5].map(rating => ({
        rating,
        count: allEvaluations.filter(e => e.communication_skill === rating).length
      })),
      overall: [1, 2, 3, 4, 5].map(rating => ({
        rating,
        count: allEvaluations.filter(e => e.overall_rating === rating).length
      }))
    };

    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="総評価数" value={stats.totalEvaluations} suffix="件" icon="fas fa-star" accent="primary" sub={`対象 ${stats.totalEngineers}名 · 今月 ${stats.thisMonth}件`} />
          <KpiCard label="平均技術力" value={Number(stats.avgTechnicalSkill) || 0} icon="fas fa-code" accent="info" sub="5段階評価" />
          <KpiCard label="平均総合評価" value={Number(stats.avgOverallRating) || 0} icon="fas fa-trophy" accent="success" sub="5段階評価" />
          <KpiCard label="高評価者" value={stats.highPerformers} suffix="名" icon="fas fa-medal" accent="warning" sub={`4-5評価 · 要改善 ${stats.needsImprovement}名`} />
        </div>

        {/* 🏆 エンジニア別成長分析 & 📊 スキル分布 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* エンジニア別成長分析 */}
          <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-700 flex items-center gap-3">
                <i className="fas fa-chart-line text-purple-500"></i>
                エンジニア別成長分析
              </h3>
              <div className="text-sm text-slate-500">
                上位{Math.min(8, engineerGrowthStats.length)}名
              </div>
            </div>
            
            <div className="space-y-4">
              {engineerGrowthStats.slice(0, 8).map((engineer, index) => (
                <div key={engineer.name} className="relative bg-gradient-to-r from-white to-slate-50 rounded-xl p-5 border border-slate-200 hover:shadow-lg transition-all duration-300 group">
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-center">
                    {/* ランキング & 基本情報 */}
                    <div className="lg:col-span-3 flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center font-bold text-white shadow-lg ${
                        index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-500' :
                        index === 1 ? 'bg-gradient-to-br from-gray-400 to-gray-500' :
                        index === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700' :
                        'bg-gradient-to-br from-slate-400 to-slate-500'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <h4 className="font-bold text-slate-800">{engineer.name}</h4>
                        <div className="text-xs text-slate-500">
                          {engineer.evaluationCount}回評価済み
                        </div>
                      </div>
                    </div>

                    {/* スキルスコア */}
                    <div className="lg:col-span-4 grid grid-cols-3 gap-2">
                      <div className="text-center bg-blue-50 rounded-lg p-2">
                        <div className="text-xs text-blue-600 mb-1">技術</div>
                        <div className="font-bold text-blue-800">{engineer.latestTechnical || '-'}</div>
                      </div>
                      <div className="text-center bg-green-50 rounded-lg p-2">
                        <div className="text-xs text-green-600 mb-1">コミュ</div>
                        <div className="font-bold text-green-800">{engineer.latestCommunication || '-'}</div>
                      </div>
                      <div className="text-center bg-purple-50 rounded-lg p-2">
                        <div className="text-xs text-purple-600 mb-1">総合</div>
                        <div className="font-bold text-purple-800">{engineer.latestOverall || '-'}</div>
                      </div>
                    </div>

                    {/* 成長トレンド */}
                    <div className="lg:col-span-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-slate-600">成長度</span>
                        <div className={`flex items-center gap-1 ${
                          engineer.growth > 0 ? 'text-emerald-600' :
                          engineer.growth < 0 ? 'text-red-600' :
                          'text-slate-500'
                        }`}>
                          <i className={`fas ${
                            engineer.growth > 0 ? 'fa-arrow-up' :
                            engineer.growth < 0 ? 'fa-arrow-down' :
                            'fa-minus'
                          } text-xs`}></i>
                          <span className="text-sm font-bold">
                            {engineer.growth > 0 ? '+' : ''}{engineer.growth}
                          </span>
                        </div>
                      </div>
                      <div className="text-xs text-slate-500">
                        最終評価: {engineer.lastEvaluationDate ? new Date(engineer.lastEvaluationDate).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' }) : '-'}
                      </div>
                    </div>

                    {/* ステータス */}
                    <div className="lg:col-span-2 flex flex-col gap-1">
                      {engineer.hasStrengths && (
                        <div className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs text-center">
                          強み記録済
                        </div>
                      )}
                      {engineer.hasGoals && (
                        <div className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs text-center">
                          目標設定済
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* スキル分布チャート */}
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <h3 className="text-xl font-bold text-slate-700 mb-6 flex items-center gap-3">
              <i className="fas fa-chart-bar text-blue-500"></i>
              スキル分布
            </h3>
            
            <div className="space-y-6">
              {/* 技術力分布 */}
              <div>
                <h4 className="font-semibold text-slate-600 mb-3 flex items-center gap-2">
                  <i className="fas fa-code text-blue-500"></i>
                  技術力
                </h4>
                <div className="space-y-2">
                  {skillDistribution.technical.map(item => (
                    <div key={item.rating} className="flex items-center gap-3">
                      <span className="text-sm font-medium w-8">{item.rating}★</span>
                      <div className="flex-1 bg-slate-200 rounded-full h-3">
                        <div 
                          className="bg-gradient-to-r from-blue-400 to-blue-500 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${stats.totalEvaluations > 0 ? (item.count / stats.totalEvaluations) * 100 : 0}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-slate-500 w-8">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* コミュニケーション力分布 */}
              <div>
                <h4 className="font-semibold text-slate-600 mb-3 flex items-center gap-2">
                  <i className="fas fa-comments text-green-500"></i>
                  コミュ力
                </h4>
                <div className="space-y-2">
                  {skillDistribution.communication.map(item => (
                    <div key={item.rating} className="flex items-center gap-3">
                      <span className="text-sm font-medium w-8">{item.rating}★</span>
                      <div className="flex-1 bg-slate-200 rounded-full h-3">
                        <div 
                          className="bg-gradient-to-r from-green-400 to-green-500 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${stats.totalEvaluations > 0 ? (item.count / stats.totalEvaluations) * 100 : 0}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-slate-500 w-8">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 総合評価分布 */}
              <div>
                <h4 className="font-semibold text-slate-600 mb-3 flex items-center gap-2">
                  <i className="fas fa-trophy text-purple-500"></i>
                  総合評価
                </h4>
                <div className="space-y-2">
                  {skillDistribution.overall.map(item => (
                    <div key={item.rating} className="flex items-center gap-3">
                      <span className="text-sm font-medium w-8">{item.rating}★</span>
                      <div className="flex-1 bg-slate-200 rounded-full h-3">
                        <div 
                          className="bg-gradient-to-r from-purple-400 to-purple-500 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${stats.totalEvaluations > 0 ? (item.count / stats.totalEvaluations) * 100 : 0}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-slate-500 w-8">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 📈 評価トレンド & 🎯 改善アクション */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 評価の質 */}
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <h3 className="text-xl font-bold text-slate-700 mb-6 flex items-center gap-3">
              <i className="fas fa-clipboard-check text-indigo-500"></i>
              評価の質分析
            </h3>
            
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-3 mb-2">
                  <i className="fas fa-thumbs-up text-emerald-600"></i>
                  <h4 className="font-semibold text-slate-700">強み記録率</h4>
                </div>
                <div className="text-2xl font-bold text-emerald-600">
                  {Math.round((allEvaluations.filter(e => e.strengths).length / allEvaluations.length) * 100)}%
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  {allEvaluations.filter(e => e.strengths).length}/{allEvaluations.length} 件で強みを記録
                </p>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
                <div className="flex items-center gap-3 mb-2">
                  <i className="fas fa-target text-blue-600"></i>
                  <h4 className="font-semibold text-slate-700">目標設定率</h4>
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {Math.round((allEvaluations.filter(e => e.goals_next_period).length / allEvaluations.length) * 100)}%
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  {allEvaluations.filter(e => e.goals_next_period).length}/{allEvaluations.length} 件で目標を設定
                </p>
              </div>

              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
                <div className="flex items-center gap-3 mb-2">
                  <i className="fas fa-exclamation-triangle text-amber-600"></i>
                  <h4 className="font-semibold text-slate-700">改善領域記録率</h4>
                </div>
                <div className="text-2xl font-bold text-amber-600">
                  {Math.round((allEvaluations.filter(e => e.improvement_areas).length / allEvaluations.length) * 100)}%
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  {allEvaluations.filter(e => e.improvement_areas).length}/{allEvaluations.length} 件で改善点を記録
                </p>
              </div>
            </div>
          </div>

          {/* 組織の強み・弱み */}
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <h3 className="text-xl font-bold text-slate-700 mb-6 flex items-center gap-3">
              <i className="fas fa-building text-purple-500"></i>
              組織分析
            </h3>
            
            <div className="space-y-4">
              <div className="p-4 bg-gradient-to-r from-emerald-50 to-green-50 rounded-xl border border-emerald-200">
                <h4 className="font-semibold text-emerald-700 mb-3 flex items-center gap-2">
                  <i className="fas fa-arrow-up"></i>
                  組織の強み
                </h4>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-center gap-2">
                    <i className="fas fa-check text-emerald-500"></i>
                    平均技術力: {stats.avgTechnicalSkill}/5.0
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fas fa-check text-emerald-500"></i>
                    高評価者: {stats.highPerformers}名 ({Math.round((stats.highPerformers / allEvaluations.length) * 100)}%)
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fas fa-check text-emerald-500"></i>
                    継続評価率: {Math.round((Object.values(groupedByEngineer).filter(evals => evals.length >= 2).length / Object.keys(groupedByEngineer).length) * 100)}%
                  </li>
                </ul>
              </div>

              <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 rounded-xl border border-red-200">
                <h4 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                  <i className="fas fa-arrow-down"></i>
                  改善エリア
                </h4>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex items-center gap-2">
                    <i className="fas fa-exclamation text-red-500"></i>
                    要改善: {stats.needsImprovement}名の低評価者
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fas fa-exclamation text-red-500"></i>
                    コミュニケーション平均: {stats.avgCommunicationSkill}/5.0
                  </li>
                  <li className="flex items-center gap-2">
                    <i className="fas fa-exclamation text-red-500"></i>
                    成長停滞: {engineerGrowthStats.filter(e => e.growth <= 0).length}名
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full bg-prodia-background">
        <LoadingState message="人事評価データを読み込み中..." className="flex-1" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-prodia-background">
      <PageHeader
        title="人事評価"
        description="エンジニア別評価面談記録・人事考課データ"
        actions={
          <>
            <div className="flex bg-prodia-background border border-prodia-border rounded-lg p-1">
              <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${viewMode === 'list' ? 'bg-prodia-surface text-prodia-primary shadow-prodia' : 'text-prodia-text-secondary hover:text-prodia-text-primary'}`}>
                <i className="fas fa-list text-[10px]"></i>リスト
              </button>
              <button onClick={() => setViewMode('dashboard')} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${viewMode === 'dashboard' ? 'bg-prodia-surface text-prodia-primary shadow-prodia' : 'text-prodia-text-secondary hover:text-prodia-text-primary'}`}>
                <i className="fas fa-chart-bar text-[10px]"></i>ダッシュボード
              </button>
            </div>
            <Button variant="secondary" size="sm" onClick={exportToExcel}>
              <i className="fas fa-file-excel text-[10px]"></i>Excel出力
            </Button>
            <Button size="sm" onClick={() => { setShowForm(true); setEditingEvaluation(null); resetForm(); }}>
              <i className="fas fa-plus text-[10px]"></i>新規評価記録
            </Button>
          </>
        }
      />
      <div className="flex-1 overflow-auto px-4 sm:px-6 py-5">

      {error && (
        <div className="mb-6">
          <Alert variant="danger">{error}</Alert>
        </div>
      )}

      {/* フィルター */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select
          value={filters.engineer_id}
          onChange={(e) => setFilters({ ...filters, engineer_id: e.target.value })}
          className="px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-400 focus:border-purple-400 bg-white"
        >
          <option value="">全エンジニア</option>
          {engineers.map(engineer => (
            <option key={engineer.id} value={engineer.id}>{engineer.name}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className="px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-400 bg-white"
          />
          <span className="text-slate-400 text-sm">〜</span>
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className="px-3 py-2 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-purple-400 bg-white"
          />
        </div>
        <button
          onClick={fetchEvaluations}
          className="px-4 py-2 text-sm bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition-colors flex items-center gap-2"
        >
          <i className="fas fa-search text-xs"></i>検索
        </button>
        {(filters.engineer_id || filters.date_from || filters.date_to) && (
          <button
            onClick={() => setFilters(f => ({ ...f, engineer_id: '', date_from: '', date_to: '' }))}
            className="px-3 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors flex items-center gap-1.5"
          >
            <i className="fas fa-times text-xs"></i>クリア
          </button>
        )}
      </div>

      {/* メインコンテンツ */}
      {viewMode === 'dashboard' ? (
        renderDashboardView()
      ) : (
        (() => {
          const filteredNames = Object.keys(groupedByEngineer)
            .filter(name =>
              !filters.engineer_id ||
              groupedByEngineer[name].some(ev => ev.engineer === parseInt(filters.engineer_id))
            )
            .sort();
          const totalEvals = filteredNames.reduce((acc, n) => acc + groupedByEngineer[n].length, 0);
          if (filteredNames.length === 0) {
            return (
              <EmptyState icon="fas fa-star" title="評価記録がありません" />
            );
          }
          return (
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
              {/* 集計 */}
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                <span className="text-xs text-slate-500">
                  <span className="font-semibold text-slate-700">{filteredNames.length}名</span>
                  {" / 計"}<span className="font-semibold text-slate-700 mx-0.5">{totalEvals}</span>件
                </span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setExpandedEngineers(new Set(filteredNames))}
                    className="text-xs text-purple-600 hover:text-purple-700 px-2.5 py-1 rounded-lg hover:bg-purple-50 transition-colors"
                  >全て展開</button>
                  <button
                    onClick={() => setExpandedEngineers(new Set())}
                    className="text-xs text-slate-400 hover:text-slate-600 px-2.5 py-1 rounded-lg hover:bg-slate-100 transition-colors"
                  >全て閉じる</button>
                </div>
              </div>

              {filteredNames.map(engineerName => {
                const evals = groupedByEngineer[engineerName];
                const latest = evals[0];
                const isExpanded = expandedEngineers.has(engineerName);
                const isAtRisk = latest && (
                  (latest.overall_rating !== null && latest.overall_rating !== undefined && Number(latest.overall_rating) <= 2) ||
                  (new Date() - new Date(latest.interview_date) > 90 * 24 * 60 * 60 * 1000)
                );
                const stars = (val, color) => Array.from({ length: 5 }).map((_, i) => (
                  <i key={i} className={`fas fa-star text-[10px] ${i < Number(val) ? color : 'text-slate-200'}`} />
                ));

                return (
                  <div key={engineerName} className="border-b border-slate-100 last:border-0">
                    {/* エンジニア行 */}
                    <div
                      className={`px-5 py-3.5 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors ${isExpanded ? 'bg-purple-50/30' : ''}`}
                      onClick={() => toggleEngineer(engineerName)}
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {engineerName.charAt(0)}
                      </div>
                      <span className="font-semibold text-slate-800 w-28 flex-shrink-0 truncate">{engineerName}</span>
                      <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full flex-shrink-0">{evals.length}件</span>
                      {latest?.overall_rating && (
                        <span className="flex items-center gap-0.5">
                          {stars(latest.overall_rating, 'text-amber-400')}
                        </span>
                      )}
                      {isAtRisk && (
                        <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-semibold flex items-center gap-1">
                          <i className="fas fa-exclamation-triangle text-[9px]"></i>要注意
                        </span>
                      )}
                      <span className="text-xs text-slate-400 ml-auto flex-shrink-0">最終: {latest?.interview_date || '--'}</span>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          setShowForm(true);
                          setEditingEvaluation(null);
                          setFormData(prev => ({ ...prev, engineer: latest?.engineer || '' }));
                        }}
                        className="text-xs text-purple-600 hover:text-white hover:bg-purple-500 border border-purple-200 px-2.5 py-1 rounded-lg transition-all flex items-center gap-1 flex-shrink-0"
                      >
                        <i className="fas fa-plus text-[9px]"></i>評価追加
                      </button>
                      <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-xs text-slate-400 flex-shrink-0`} />
                    </div>

                    {/* 展開: 評価テーブル */}
                    {isExpanded && (
                      <div className="border-t border-slate-100">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-50 text-xs text-slate-400 font-semibold">
                              <th className="px-5 py-2 text-left">評価日</th>
                              <th className="px-3 py-2 text-center">総合</th>
                              <th className="px-3 py-2 text-center">技術力</th>
                              <th className="px-3 py-2 text-center">コミュ力</th>
                              <th className="px-3 py-2 text-center">モチベ</th>
                              <th className="px-3 py-2 text-center">操作</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {evals.map((evaluation, idx) => (
                              <tr
                                key={evaluation.id}
                                onClick={() => setSelectedEvaluation(evaluation)}
                                className="hover:bg-indigo-50/40 cursor-pointer transition-colors"
                              >
                                <td className="px-5 py-2.5">
                                  <div className="flex items-center gap-2">
                                    {idx === 0 && <span className="text-[9px] bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded-full font-bold">最新</span>}
                                    <span className="text-slate-700 font-medium">{evaluation.interview_date}</span>
                                  </div>
                                </td>
                                <td className="px-3 py-2.5 text-center">
                                  <span className="flex items-center justify-center gap-0.5">{evaluation.overall_rating ? stars(evaluation.overall_rating, 'text-amber-400') : <span className="text-slate-300 text-xs">--</span>}</span>
                                </td>
                                <td className="px-3 py-2.5 text-center">
                                  <span className="flex items-center justify-center gap-0.5">{evaluation.technical_skill ? stars(evaluation.technical_skill, 'text-blue-400') : <span className="text-slate-300 text-xs">--</span>}</span>
                                </td>
                                <td className="px-3 py-2.5 text-center">
                                  <span className="flex items-center justify-center gap-0.5">{evaluation.communication_skill ? stars(evaluation.communication_skill, 'text-green-400') : <span className="text-slate-300 text-xs">--</span>}</span>
                                </td>
                                <td className="px-3 py-2.5 text-center">
                                  <span className="flex items-center justify-center gap-0.5">{evaluation.motivation ? stars(evaluation.motivation, 'text-orange-400') : <span className="text-slate-300 text-xs">--</span>}</span>
                                </td>
                                <td className="px-3 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                                  <div className="flex items-center justify-center gap-1">
                                    <button
                                      onClick={e => { e.stopPropagation(); handleEdit(evaluation); }}
                                      className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-blue-50 text-slate-400 hover:text-blue-600 flex items-center justify-center transition-colors"
                                      title="編集"
                                    >
                                      <i className="fas fa-pen text-[10px]"></i>
                                    </button>
                                    {confirmDeleteId === evaluation.id ? (
                                      <div className="flex items-center gap-1 bg-red-50 px-2 py-1 rounded-lg border border-red-200">
                                        <span className="text-xs text-red-600 whitespace-nowrap">削除?</span>
                                        <button onClick={e => { e.stopPropagation(); handleDelete(evaluation.id); }} className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded hover:bg-red-600">はい</button>
                                        <button onClick={e => { e.stopPropagation(); setConfirmDeleteId(null); }} className="text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded hover:bg-slate-300">いいえ</button>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={e => { e.stopPropagation(); setConfirmDeleteId(evaluation.id); }}
                                        className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-red-50 text-slate-400 hover:text-red-500 flex items-center justify-center transition-colors"
                                        title="削除"
                                      >
                                        <i className="fas fa-trash text-[10px]"></i>
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()
      )}

      {/* 評価詳細モーダル */}
      {selectedEvaluation && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setSelectedEvaluation(null)}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200 bg-gradient-to-r from-purple-50 to-indigo-50">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-800">{selectedEvaluation.engineer_name} - 評価詳細</h3>
                  <p className="text-xs text-slate-500 mt-0.5">評価日: {selectedEvaluation.interview_date}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { handleEdit(selectedEvaluation); setSelectedEvaluation(null); }}
                    className="px-3 py-1.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg text-sm font-medium hover:shadow-md transition-all flex items-center gap-1.5"
                  >
                    <i className="fas fa-edit text-xs"></i>編集
                  </button>
                  <button onClick={() => setSelectedEvaluation(null)} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                    <i className="fas fa-times text-lg"></i>
                  </button>
                </div>
              </div>
            </div>
            <div className="p-5 space-y-5">
              {/* スキル評価 */}
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">スキル評価</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {[
                    { key: 'technical_skill', label: '技術力', icon: 'fa-code' },
                    { key: 'communication_skill', label: 'コミュ力', icon: 'fa-comments' },
                    { key: 'motivation', label: 'やる気', icon: 'fa-fire' },
                    { key: 'leadership', label: 'リーダーシップ', icon: 'fa-users' },
                    { key: 'problem_solving', label: '問題解決力', icon: 'fa-lightbulb' },
                    { key: 'overall_rating', label: '総合評価', icon: 'fa-trophy' },
                  ].map(({ key, label, icon }) => (
                    <div key={key} className="bg-slate-50 rounded-xl p-3 text-center border border-slate-200">
                      <div className="flex items-center justify-center gap-1 mb-1.5">
                        <i className={`fas ${icon} text-slate-400 text-xs`}></i>
                        <span className="text-xs text-slate-500 font-medium">{label}</span>
                      </div>
                      <div className="flex justify-center gap-0.5 mb-1">
                        {[1,2,3,4,5].map(n => (
                          <span key={n} className={`text-sm ${n <= Number(selectedEvaluation[key] || 0) ? 'text-amber-400' : 'text-slate-200'}`}>★</span>
                        ))}
                      </div>
                      <div className={`text-xs font-semibold ${
                        !selectedEvaluation[key] ? 'text-slate-400' :
                        Number(selectedEvaluation[key]) >= 4 ? 'text-emerald-600' :
                        Number(selectedEvaluation[key]) <= 2 ? 'text-red-500' :
                        'text-slate-600'
                      }`}>
                        {selectedEvaluation[key] ? getSkillRatingText(selectedEvaluation[key]) : '未評価'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* テキスト評価 */}
              {[
                { key: 'strengths', label: '強み・評価点', icon: 'fa-thumbs-up' },
                { key: 'improvement_areas', label: '改善領域', icon: 'fa-exclamation-triangle' },
                { key: 'goals_next_period', label: '次期目標', icon: 'fa-bullseye' },
                { key: 'notes', label: '備考', icon: 'fa-sticky-note' },
              ].filter(({ key }) => selectedEvaluation[key]).map(({ key, label, icon }) => (
                <div key={key}>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <i className={`fas ${icon} text-slate-400`}></i>{label}
                  </p>
                  <p className="text-sm text-slate-700 bg-slate-50 rounded-xl p-4 border border-slate-200 whitespace-pre-wrap leading-relaxed">{selectedEvaluation[key]}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 人事評価フォームモーダル */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-200">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold text-slate-700">
                  {editingEvaluation ? '人事評価編集' : '新規人事評価'}
                </h3>
                <button
                  onClick={() => {
                    setShowForm(false);
                    setEditingEvaluation(null);
                    resetForm();
                  }}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors duration-200"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="p-8 space-y-8">
              
              {/* 評価基本情報セクション */}
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-2xl border border-purple-100">
                <h4 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <i className="fas fa-user-check text-purple-600"></i>
                  評価基本情報
                </h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-2">
                      <i className="fas fa-user text-purple-500 text-xs"></i>
                      エンジニア *
                    </label>
                    <div className="relative">
                      <div
                        className="w-full px-4 py-3 border border-purple-200 rounded-xl bg-white shadow-sm flex items-center gap-2 cursor-text"
                        onClick={() => setShowEngineerDropdown(true)}
                      >
                        {formData.engineer && !showEngineerDropdown ? (
                          <span className="flex-1 text-slate-800">
                            {engineers.find(e => String(e.id) === String(formData.engineer))?.name || ""}
                          </span>
                        ) : (
                          <input
                            type="text"
                            autoFocus={showEngineerDropdown}
                            value={engineerSearch}
                            onChange={e => { setEngineerSearch(e.target.value); setShowEngineerDropdown(true); }}
                            onFocus={() => setShowEngineerDropdown(true)}
                            placeholder={formData.engineer ? engineers.find(e => String(e.id) === String(formData.engineer))?.name : "名前で検索..."}
                            className="flex-1 outline-none text-sm bg-transparent"
                          />
                        )}
                        <i className="fas fa-search text-purple-400 text-xs flex-shrink-0"></i>
                      </div>
                      {showEngineerDropdown && (
                        <>
                          <div className="fixed inset-0 z-10" onClick={() => { setShowEngineerDropdown(false); setEngineerSearch(""); }} />
                          <ul className="absolute z-20 mt-1 w-full bg-white border border-purple-200 rounded-xl shadow-xl max-h-52 overflow-y-auto">
                            {engineers
                              .filter(e => e.name.includes(engineerSearch))
                              .map(engineer => (
                                <li
                                  key={engineer.id}
                                  onClick={() => {
                                    setFormData({ ...formData, engineer: engineer.id });
                                    setEngineerSearch("");
                                    setShowEngineerDropdown(false);
                                  }}
                                  className={`px-4 py-2.5 text-sm cursor-pointer hover:bg-purple-50 hover:text-purple-700 transition-colors ${
                                    String(formData.engineer) === String(engineer.id) ? "bg-purple-50 text-purple-700 font-semibold" : "text-slate-700"
                                  }`}
                                >
                                  {engineer.name}
                                </li>
                              ))
                            }
                            {engineers.filter(e => e.name.includes(engineerSearch)).length === 0 && (
                              <li className="px-4 py-3 text-sm text-slate-400 text-center">該当なし</li>
                            )}
                          </ul>
                        </>
                      )}
                      {/* hidden input for required validation */}
                      <input type="text" required value={formData.engineer} onChange={() => {}} className="sr-only" />
                    </div>
                  </div>
                  
                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-2">
                      <i className="fas fa-calendar text-purple-500 text-xs"></i>
                      評価日 *
                    </label>
                    <input
                      type="date"
                      value={formData.evaluation_date}
                      onChange={(e) => setFormData({ ...formData, evaluation_date: e.target.value })}
                      required
                      className="w-full px-4 py-3 border border-purple-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white shadow-sm transition-all duration-200"
                    />
                  </div>
                </div>
              </div>

              {/* スキル評価セクション */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-2xl border border-blue-100">
                <h4 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <i className="fas fa-chart-line text-blue-600"></i>
                  スキル評価（★をタップして評価）
                </h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                  {[
                    { field: 'technical_skill', label: '技術力', icon: 'fa-code' },
                    { field: 'communication_skill', label: 'コミュニケーション力', icon: 'fa-comments' },
                    { field: 'motivation', label: 'やる気・意欲', icon: 'fa-fire' },
                    { field: 'leadership', label: 'リーダーシップ', icon: 'fa-users' },
                    { field: 'problem_solving', label: '問題解決力', icon: 'fa-lightbulb' },
                    { field: 'overall_rating', label: '総合評価', icon: 'fa-trophy' },
                  ].map(({ field, label, icon }) => (
                    <div key={field} className="bg-white/70 rounded-xl p-3 border border-blue-100">
                      <label className="flex items-center gap-1.5 text-sm font-medium text-slate-700 mb-2">
                        <i className={`fas ${icon} text-blue-500 text-xs`}></i>
                        {label}
                      </label>
                      <div className="flex items-center gap-1 flex-wrap">
                        {[1, 2, 3, 4, 5].map(n => (
                          <button
                            type="button"
                            key={n}
                            onClick={() => setFormData(f => ({ ...f, [field]: String(n) }))}
                            className={`w-9 h-9 flex items-center justify-center rounded-lg text-xl transition-all duration-150 ${
                              parseInt(formData[field]) >= n
                                ? 'text-amber-400 bg-amber-50 shadow-sm scale-110'
                                : 'text-slate-300 hover:text-amber-300 hover:bg-amber-50/50'
                            }`}
                          >
                            ★
                          </button>
                        ))}
                        {formData[field] && (
                          <>
                            <span className="ml-1 text-xs text-slate-500 bg-white px-2 py-1 rounded-lg border border-slate-200">
                              {['', '要改善', 'やや不足', '標準', '良好', '優秀'][parseInt(formData[field])]}
                            </span>
                            <button
                              type="button"
                              onClick={() => setFormData(f => ({ ...f, [field]: '' }))}
                              className="text-slate-300 hover:text-red-400 transition-colors p-1"
                            >
                              <i className="fas fa-times text-xs"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 定性評価セクション */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-100">
                <h4 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <i className="fas fa-comment-alt text-green-600"></i>
                  定性評価・コメント
                </h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-2">
                      <i className="fas fa-thumbs-up text-green-500 text-xs"></i>
                      強み・評価点
                    </label>
                    <textarea
                      value={formData.strengths}
                      onChange={(e) => setFormData({ ...formData, strengths: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-3 border border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white shadow-sm transition-all duration-200"
                      placeholder="優れている点、強みを具体的に記載してください..."
                    />
                  </div>
                  
                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-2">
                      <i className="fas fa-exclamation-triangle text-amber-500 text-xs"></i>
                      改善領域
                    </label>
                    <textarea
                      value={formData.improvement_areas}
                      onChange={(e) => setFormData({ ...formData, improvement_areas: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-3 border border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white shadow-sm transition-all duration-200"
                      placeholder="改善が必要な領域を記載してください..."
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-2">
                      <i className="fas fa-target text-green-500 text-xs"></i>
                      次期目標
                    </label>
                    <textarea
                      value={formData.goals_next_period}
                      onChange={(e) => setFormData({ ...formData, goals_next_period: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-3 border border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white shadow-sm transition-all duration-200"
                      placeholder="次の評価期間での目標・期待値を記載してください..."
                    />
                  </div>

                  <div>
                    <label className="flex items-center gap-1 text-sm font-medium text-slate-700 mb-2">
                      <i className="fas fa-sticky-note text-green-500 text-xs"></i>
                      備考・その他
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={4}
                      className="w-full px-4 py-3 border border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white shadow-sm transition-all duration-200"
                      placeholder="その他メモを記載してください..."
                    />
                  </div>
                </div>
              </div>

              {/* アクションボタンセクション */}
              <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-6 rounded-2xl border border-slate-100 mt-6">
                <div className="flex flex-col sm:flex-row justify-end gap-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditingEvaluation(null);
                      resetForm();
                    }}
                    className="px-8 py-4 bg-gradient-to-r from-slate-100 to-gray-100 text-slate-700 rounded-xl font-semibold shadow-md hover:shadow-lg hover:from-slate-200 hover:to-gray-200 transition-all duration-200 flex items-center justify-center gap-2 border border-slate-200"
                  >
                    <i className="fas fa-times text-slate-500"></i>
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-4 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl hover:from-purple-600 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center gap-2 transform hover:scale-105"
                  >
                    <i className={`fas ${editingEvaluation ? 'fa-edit' : 'fa-save'} text-white`}></i>
                    {editingEvaluation ? '評価を更新' : '評価を保存'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}